import React, { createContext, useCallback, useContext, useId, useRef, useState } from 'react';
import { Button } from './Button';
import { useModalFocus } from './focus';

// Replaces window.confirm / alert() (ADR-49):
//   const confirm = useConfirm();
//   if (await confirm({ title: 'Delete x?', body: '…', confirmLabel: 'Delete', tone: 'danger' })) …
// Focus starts on the safe choice (Cancel); Escape cancels.

export interface ConfirmOptions {
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
}

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

const ConfirmContext = createContext<((o: ConfirmOptions) => Promise<boolean>) | null>(null);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<Pending | null>(null);
  const confirm = useCallback(
    (o: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ ...o, resolve })),
    [],
  );
  const close = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && <ConfirmDialog options={pending} onClose={close} />}
    </ConfirmContext.Provider>
  );
};

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}

const ConfirmDialog: React.FC<{ options: ConfirmOptions; onClose: (ok: boolean) => void }> = ({ options, onClose }) => {
  const box = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const bodyId = useId();
  useModalFocus(true, box, () => onClose(false), cancel);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-scrim px-4 pt-32">
      <div
        ref={box}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={options.body ? bodyId : undefined}
        className="w-full max-w-md rounded-xl bg-surface p-6 text-body shadow-2xl"
      >
        <h2 id={titleId} className="text-lg font-semibold">{options.title}</h2>
        {options.body && <div id={bodyId} className="mt-2 text-sm text-muted">{options.body}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <Button ref={cancel} onClick={() => onClose(false)}>{options.cancelLabel ?? 'Cancel'}</Button>
          <Button variant={options.tone === 'primary' ? 'primary' : 'danger'} onClick={() => onClose(true)}>
            {options.confirmLabel ?? 'Confirm'}
          </Button>
        </div>
      </div>
    </div>
  );
};
